// API utility for fetching the Bunker Desk route-options snapshot behind a
// VMS deep-link. The page is deliberately NOT live: the services public API
// serves a snapshot stored at VMS-call time and answers 410 once its TTL has
// passed — the only remedy is reopening the link from the VMS.

import { useCallback } from 'react';

const API_BASE_URL = 'https://xo2a24ey5h.execute-api.eu-west-1.amazonaws.com';

// TypeScript interfaces — mirrors docs/bunker-desk-integration.md (services)
// and the voycalc repo's docs/integrations-api.md §2.5 (full price rows).
export interface WaypointData {
  lat: number;
  lon: number;
}

export interface AvailabilityStatus {
  value: string;
  label: string;
  color: 'green' | 'red' | 'yellow' | 'gray' | 'blue';
}

export interface AvailabilityEntry {
  level?: string | null;
  comment?: string | null;
  updatedBy?: string;
  updatedAt?: string;
}

export interface ForwardCurvePoint {
  date: string; // "YYYY-MM"
  value: number;
}

export interface BunkerRow {
  bunkerTypeId: string;
  bunkerTypeName: string;
  spot: { enabled: boolean; source: string; value: number | null; asOf: string | null };
  forward: { enabled: boolean; source: string; curve: ForwardCurvePoint[]; flatValue: number | null };
  availability: { spot?: AvailabilityEntry | null; forward?: AvailabilityEntry | null };
  fetch: {
    lastFetchAt: string | null;
    lastChangeAt: string | null;
    prevValue: number | null;
    changePercent: number | null;
  };
  updatedAt: string | null;
}

export interface BunkerPort {
  portNo: string;
  portName: string;
  lat: number;
  lon: number;
  deviationNm: number;
  nearestRouteIndex: number;
  bunkers: BunkerRow[];
}

export interface VoyageMeta {
  routeDesc?: string | null;
  [key: string]: unknown;
}

export interface BunkerRouteResponse {
  wp: WaypointData[] | WaypointData[][];
  voyage_meta?: VoyageMeta | null;
  radiusNm: number;
  asOf: string;
  availabilityStatuses?: AvailabilityStatus[];
  ports: BunkerPort[];
}

export interface ApiResult<T = unknown> {
  ok: boolean;
  status: number;
  data?: T;
  message?: string;
  /** true when the snapshot's TTL has passed (HTTP 410) or the cache item is
   *  gone (HTTP 404) — the user must reopen the link from their VMS. */
  expired?: boolean;
}

export const useBunkerRouteApi = () => {
  const fetchBunkerRoute = useCallback(
    async (id: string, src?: string | null): Promise<ApiResult<BunkerRouteResponse>> => {
      const params = new URLSearchParams({ type: 'bunker', id });
      if (src) params.set('src', src);
      const url = `${API_BASE_URL}/api?${params.toString()}`;

      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        let data: any = null;
        try {
          data = await response.json();
        } catch {
          // non-JSON error body — fall through with data = null
        }

        if (!response.ok) {
          return {
            ok: false,
            status: response.status,
            expired: response.status === 410 || response.status === 404,
            message: data?.message || data?.error || 'Failed to load bunker data',
          };
        }

        return { ok: true, status: response.status, data };
      } catch (error) {
        return {
          ok: false,
          status: 0,
          message: error instanceof Error ? error.message : 'Network error',
        };
      }
    },
    []
  );

  return { fetchBunkerRoute };
};

// Fallback when the org has not configured a status catalog — mirrors the
// desk's DEFAULT_AVAILABILITY_STATUSES in the app.
export const DEFAULT_AVAILABILITY_STATUSES: AvailabilityStatus[] = [
  { value: 'ok', label: 'Ok', color: 'green' },
  { value: 'check', label: 'Check', color: 'red' },
];
